import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import StatCard from '../../components/admin/StatCard';
import StatusBadge from '../../components/appointments/StatusBadge';
import OrderStatusStepper from '../../components/orders/OrderStatusStepper';
import { fetchClientDashboard } from '../../api/clientHistory';
import DashboardShell from '../../components/ui/DashboardShell';
import { CardSkeleton } from '../../components/ui/Skeleton';
import { localeDate } from '../../i18n/helpers';

const formatLkr = (n) => `LKR ${Number(n || 0).toLocaleString()}`;

const SectionHeader = ({ title, linkTo, linkLabel }) => (
  <div className="mb-4 flex items-center justify-between gap-3">
    <h2 className="text-lg font-medium text-zinc-900 dark:text-zinc-100">{title}</h2>
    {linkTo && (
      <Link to={linkTo} className="text-sm text-glow-400 hover:underline">
        {linkLabel}
      </Link>
    )}
  </div>
);

const ClientDashboard = () => {
  const { t } = useTranslation();

  const { data, isLoading } = useQuery({
    queryKey: ['client-dashboard'],
    queryFn: fetchClientDashboard,
    staleTime: 60_000,
  });

  const summary = data?.summary;
  const upcoming = data?.upcoming || [];
  const latestServices = data?.latestServices || [];
  const latestProducts = data?.latestProducts || [];
  const recentActivity = data?.recentActivity || [];

  const formatDate = (d) =>
    d ? localeDate(d, { day: 'numeric', month: 'short', year: 'numeric' }) : '';

  return (
    <DashboardShell title={t('client.dashboard.title')} subtitle={t('client.dashboard.subtitle')}>
      <div className="mb-8 flex flex-wrap gap-3">
        <Link
          to="/book"
          className="rounded-lg bg-glow-600 px-4 py-2 text-sm font-medium text-white hover:bg-glow-500"
        >
          {t('client.dashboard.quickBook')}
        </Link>
        <Link
          to="/marketplace"
          className="rounded-lg border border-zinc-300 px-4 py-2 text-sm dark:border-zinc-700 hover:bg-zinc-100 dark:hover:bg-zinc-800"
        >
          {t('client.dashboard.browseMarketplace')}
        </Link>
        <Link
          to="/client/history"
          className="rounded-lg border border-zinc-300 px-4 py-2 text-sm dark:border-zinc-700 hover:bg-zinc-100 dark:hover:bg-zinc-800"
        >
          {t('client.dashboard.viewHistory')}
        </Link>
      </div>

      {isLoading && (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {Array.from({ length: 4 }).map((_, i) => (
            <CardSkeleton key={i} />
          ))}
        </div>
      )}

      {!isLoading && summary && (
        <>
          <div className="mb-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <StatCard
              label={t('client.dashboard.totalSpend')}
              value={formatLkr(summary.totalSpend)}
              icon="💳"
              trend={t('client.dashboard.spendBreakdown', {
                services: formatLkr(summary.servicesSpend),
                products: formatLkr(summary.productsSpend),
              })}
            />
            <StatCard
              label={t('client.dashboard.servicesBooked')}
              value={summary.totalServices}
              icon="✂️"
              trend={t('client.dashboard.completedCount', { count: summary.completedServices })}
            />
            <StatCard
              label={t('client.dashboard.productsOrdered')}
              value={summary.totalOrders}
              icon="🛍️"
              trend={t('client.dashboard.deliveredCount', { count: summary.deliveredOrders })}
            />
            <StatCard
              label={t('client.dashboard.upcomingAppointments')}
              value={summary.upcomingServices}
              icon="📅"
              trend={
                summary.upcomingServices > 0
                  ? t('client.dashboard.viewUpcoming')
                  : t('client.dashboard.noUpcoming')
              }
            />
          </div>

          {upcoming.length > 0 && (
            <section className="mb-8 rounded-xl border border-glow-600/30 bg-glow-600/10 p-5">
              <SectionHeader
                title={t('client.dashboard.upcomingTitle')}
                linkTo="/client/appointments"
                linkLabel={t('client.dashboard.allAppointments')}
              />
              <ul className="space-y-3">
                {upcoming.map((a) => (
                  <li
                    key={a._id}
                    className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-zinc-200/80 bg-white/80 p-4 dark:border-zinc-700 dark:bg-zinc-900/80"
                  >
                    <div>
                      <p className="font-medium">{a.serviceName}</p>
                      <p className="text-sm text-zinc-600 dark:text-zinc-400">
                        {a.salonName}
                        {a.branchName ? ` · ${a.branchName}` : ''}
                      </p>
                      <p className="mt-1 text-xs text-zinc-500">
                        {formatDate(a.date)}
                        {a.time ? ` · ${a.time}` : ''}
                      </p>
                    </div>
                    <StatusBadge status={a.status} />
                  </li>
                ))}
              </ul>
            </section>
          )}

          <div className="mb-8 grid gap-6 lg:grid-cols-2">
            <section className="rounded-xl border border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-900 p-5">
              <SectionHeader
                title={t('client.dashboard.latestServices')}
                linkTo="/client/history"
                linkLabel={t('client.dashboard.viewAll')}
              />
              {latestServices.length === 0 ? (
                <p className="text-sm text-zinc-600 dark:text-zinc-500">{t('client.dashboard.noServicesYet')}</p>
              ) : (
                <ul className="space-y-3">
                  {latestServices.map((a) => (
                    <li
                      key={a._id}
                      className="flex items-start justify-between gap-3 border-b border-zinc-100 pb-3 last:border-0 last:pb-0 dark:border-zinc-800"
                    >
                      <div className="min-w-0">
                        <p className="truncate font-medium">{a.serviceName}</p>
                        <p className="text-xs text-zinc-600 dark:text-zinc-500">{a.salonName}</p>
                        <p className="mt-1 text-xs text-zinc-500">{formatDate(a.date)}</p>
                      </div>
                      <div className="shrink-0 text-right">
                        <StatusBadge status={a.status} />
                        {a.amount > 0 && (
                          <p className="mt-2 text-xs font-medium text-glow-300">{formatLkr(a.amount)}</p>
                        )}
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </section>

            <section className="rounded-xl border border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-900 p-5">
              <SectionHeader
                title={t('client.dashboard.latestProducts')}
                linkTo="/client/history"
                linkLabel={t('client.dashboard.viewAll')}
              />
              {latestProducts.length === 0 ? (
                <p className="text-sm text-zinc-600 dark:text-zinc-500">{t('client.dashboard.noProductsYet')}</p>
              ) : (
                <ul className="space-y-4">
                  {latestProducts.map((order) => (
                    <li key={order._id} className="border-b border-zinc-100 pb-4 last:border-0 last:pb-0 dark:border-zinc-800">
                      <div className="flex items-start justify-between gap-2">
                        <div>
                          <p className="font-medium">{order.salonName}</p>
                          <p className="text-xs text-zinc-600 dark:text-zinc-500">
                            {formatDate(order.createdAt)} · {t('client.dashboard.itemCount', { count: order.itemCount })}
                          </p>
                        </div>
                        <p className="shrink-0 text-sm font-semibold text-glow-300">{formatLkr(order.totalAmount)}</p>
                      </div>
                      {order.items?.length > 0 && (
                        <p className="mt-2 text-xs text-zinc-600 dark:text-zinc-400">
                          {order.items.map((i) => `${i.productName} ×${i.quantity}`).join(', ')}
                        </p>
                      )}
                      <div className="mt-2">
                        <OrderStatusStepper status={order.status} />
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </section>
          </div>

          <section className="rounded-xl border border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-900 p-5">
            <SectionHeader
              title={t('client.dashboard.recentActivity')}
              linkTo="/client/history"
              linkLabel={t('client.dashboard.viewHistory')}
            />
            {recentActivity.length === 0 ? (
              <p className="text-sm text-zinc-600 dark:text-zinc-500">{t('client.dashboard.noActivityYet')}</p>
            ) : (
              <ul className="divide-y divide-zinc-100 dark:divide-zinc-800">
                {recentActivity.map((item) => (
                  <li key={`${item.type}-${item._id}`} className="flex gap-4 py-4 first:pt-0 last:pb-0">
                    <span className="text-xl" aria-hidden>
                      {item.type === 'service' ? '✂️' : '📦'}
                    </span>
                    <div className="min-w-0 flex-1">
                      <p className="font-medium">
                        {item.type === 'service'
                          ? item.serviceName
                          : t('client.dashboard.orderActivity', {
                              number: item._id.slice(-8).toUpperCase(),
                            })}
                      </p>
                      <p className="text-sm text-zinc-600 dark:text-zinc-400">{item.salonName}</p>
                      <p className="mt-1 text-xs text-zinc-500">
                        {formatDate(item.at || item.createdAt)}
                        {item.type === 'service' && item.status && (
                          <> · {t(`status.appointment.${item.status}`)}</>
                        )}
                      </p>
                    </div>
                    <p className="shrink-0 text-sm font-medium text-glow-300">
                      {formatLkr(item.type === 'service' ? item.amount : item.totalAmount)}
                    </p>
                  </li>
                ))}
              </ul>
            )}
          </section>
        </>
      )}
    </DashboardShell>
  );
};

export default ClientDashboard;
